import type { ReactNode } from "react";
import { cn } from "../../lib/utils";

export type DataTableColumn<T> = {
  key: string;
  header: ReactNode;
  className?: string;
  align?: "left" | "center" | "right";
  sortable?: boolean;
  sortKey?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (key: string) => void;
  cell: (row: T) => ReactNode;
  hideOnMobile?: boolean; // Nouvelle propriété pour masquer certaines colonnes sur mobile
};

export type PaginationProps = {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  ariaLabelPrevious?: string;
  ariaLabelNext?: string;
};

export type DataTableProps<T> = {
  data: T[];
  columns: DataTableColumn<T>[];
  rowKey?: (row: T, index: number) => string | number;
  emptyState?: ReactNode;
  isLoading?: boolean;
  loadingText?: string;
  onSort?: (key: string) => void;
  pagination?: PaginationProps;
};

export const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  isLoading = false,
  ariaLabelPrevious = "Previous page",
  ariaLabelNext = "Next page",
}: PaginationProps) => {
  if (totalPages <= 1) return null;

  const pageNumbers = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
    if (totalPages <= 5) {
      return i + 1;
    }
    if (currentPage <= 3) {
      return i + 1;
    }
    if (currentPage >= totalPages - 2) {
      return totalPages - 4 + i;
    }
    return currentPage - 2 + i;
  });

  return (
    <div className="flex items-center justify-center gap-2 border-t border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground dark:border-white/10 dark:bg-white/5 dark:text-white/70">
      <button
        type="button"
        aria-label={ariaLabelPrevious}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1 || isLoading}
        className="rounded-full border border-border bg-background px-3 py-1 text-muted-foreground transition hover:border-border hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:border-white/30 dark:hover:bg-white/10">
        {"<"}
      </button>
      {pageNumbers.map((pageNum) => (
        <button
          key={pageNum}
          type="button"
          onClick={() => onPageChange(pageNum)}
          disabled={isLoading}
          className={cn(
            "h-8 w-8 rounded-full border text-xs font-semibold transition",
            pageNum === currentPage
              ? "border-emerald-500 bg-emerald-500/15 text-emerald-800 dark:border-emerald-400 dark:bg-emerald-500/20 dark:text-emerald-200"
              : "border-border bg-background text-muted-foreground hover:border-border hover:bg-muted dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:border-white/30 dark:hover:bg-white/10"
          )}>
          {pageNum}
        </button>
      ))}
      <button
        type="button"
        aria-label={ariaLabelNext}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages || isLoading}
        className="rounded-full border border-border bg-background px-3 py-1 text-muted-foreground transition hover:border-border hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-white/5 dark:text-white/70 dark:hover:border-white/30 dark:hover:bg-white/10">
        {">"}
      </button>
    </div>
  );
};

export const DataTable = <T,>({
  data,
  columns,
  rowKey,
  emptyState,
  isLoading = false,
  loadingText = "Chargement…",
  onSort,
  pagination,
}: DataTableProps<T>) => {
  // Colonnes visibles sur mobile (sans hideOnMobile)
  const mobileColumns = columns.filter((col) => !col.hideOnMobile);
  
  return (
    <div className="relative overflow-hidden rounded-3xl border border-border bg-card text-foreground shadow-lg dark:border-white/10 dark:bg-black/20 dark:shadow-2xl">
      {/* Vue desktop : tableau */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border text-sm dark:divide-white/10">
          <thead className="bg-muted/50 text-muted-foreground dark:bg-white/5 dark:text-white/70">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn("px-4 py-3 text-left font-medium", column.className)}
                  onClick={column.sortable && onSort ? () => onSort(column.key) : undefined}
                  role={column.sortable && onSort ? "button" : undefined}
                  tabIndex={column.sortable && onSort ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (column.sortable && onSort && e.key === "Enter") {
                      onSort && onSort(column.key);
                    }
                  }}>
                  <div className={cn("flex items-center", column.align === "center" && "justify-center", column.align === "right" && "justify-end")}>
                    {column.header}
                    {column.sortable && (
                      <button
                        type="button"
                        className="ml-2 rounded-sm p-1 hover:bg-muted dark:hover:bg-white/10"
                        onClick={() => onSort && onSort(column.key)}
                        aria-label={`Trier par ${column.header}`}>
                        {column.sortDirection === "asc" ? "▲" : "▼"}
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border text-foreground dark:divide-white/5 dark:text-white/80">
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-6 text-center text-muted-foreground dark:text-white/60">
                  {emptyState ?? "Aucune donnée disponible."}
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr
                  key={rowKey ? rowKey(row, index) : index}
                  className="hover:bg-muted/40 dark:hover:bg-white/5">
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={cn("px-4 py-3 align-middle", column.className)}>
                      <div className={cn("flex items-center", column.align === "center" && "justify-center", column.align === "right" && "justify-end")}>
                        {column.cell(row)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Vue mobile/tablette : cartes */}
      <div className="lg:hidden">
        {data.length === 0 ? (
          <div className="px-4 py-12 text-center text-muted-foreground dark:text-white/60">
            {emptyState ?? "Aucune donnée disponible."}
          </div>
        ) : (
          <div className="p-2 space-y-3">
            {data.map((row, index) => {
              // Trouver les colonnes spécifiques pour un meilleur rendu
              const nameColumn = columns.find((col) => col.key === "name");
              const statusColumn = columns.find((col) => col.key === "status");
              const metricsColumn = columns.find((col) => col.key === "metrics");
              const actionsColumn = columns.find((col) => col.key === "actions");
              const categoryColumn = columns.find((col) => col.key === "category");
              
              return (
                <div
                  key={rowKey ? rowKey(row, index) : index}
                  className="rounded-xl border border-border bg-muted/30 p-4 shadow-md transition-all hover:border-border dark:border-white/10 dark:bg-gradient-to-br dark:from-black/40 dark:to-black/20 dark:shadow-lg dark:hover:border-white/20">
                  {/* En-tête avec image, nom et statut */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    {nameColumn && (
                      <div className="flex-1 min-w-0">
                        {nameColumn.cell(row)}
                      </div>
                    )}
                    {statusColumn && (
                      <div className="flex-shrink-0">
                        {statusColumn.cell(row)}
                      </div>
                    )}
                  </div>

                  {/* Métriques */}
                  {metricsColumn && (
                    <div className="mb-3 border-b border-border pb-3 dark:border-white/5">
                      {metricsColumn.cell(row)}
                    </div>
                  )}

                  {/* Catégorie (si disponible) */}
                  {categoryColumn && (
                    <div className="mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground dark:text-white/50">Catégorie:</span>
                        {categoryColumn.cell(row)}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {actionsColumn && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {actionsColumn.cell(row)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm dark:bg-slate-950/70">
          <p className="text-sm text-muted-foreground dark:text-white/80">{loadingText}</p>
        </div>
      )}

      {/* Pagination */}
      {pagination && (
        <Pagination {...pagination} isLoading={isLoading} />
      )}
    </div>
  );
};


